import { genericPrefixes } from "./data-types/generic-types";

/**
 * Narrower than `SchemaField`, whose `Schema["fields"]` values carry no `key` —
 * most call sites hold the key and the definition separately.
 */
export interface DisplayableVariable {
  key: string;
  label?: string;
}

/** Initialisms sentence case must not lowercase. Singular only; plurals derive. */
const initialisms = [
  "ai",
  "api",
  "csv",
  "fsa",
  "hsa",
  "html",
  "id",
  "pdf",
  "qr",
  "rma",
  "sku",
  "sms",
  "tnpl",
  "upc",
  "url",
  "utm",
];

const initialismDisplay = new Map<string, string>(
  initialisms.flatMap((initialism): [string, string][] => {
    const upper = initialism.toUpperCase();
    return [
      [initialism, upper],
      [`${initialism}s`, `${upper}s`],
    ];
  }),
);

function splitWords(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d)/g, "$1 $2")
    .split(/[\s_-]+/)
    .filter(Boolean);
}

function displayWord(word: string, isFirstWord: boolean): string {
  const lowered = word.toLowerCase();
  return (
    initialismDisplay.get(lowered) ??
    (isFirstWord ? lowered.charAt(0).toUpperCase() + lowered.slice(1) : lowered)
  );
}

/**
 * The merchant-facing name for a variable — sentence case, initialisms intact.
 * Use anywhere a variable is named in the UI; never show the raw key or token.
 */
export function variableDisplayName(field: DisplayableVariable): string {
  if (field.label) {
    return field.label;
  }
  const words = splitWords(field.key);
  return words.length
    ? words.map((word, index) => displayWord(word, index === 0)).join(" ")
    : field.key;
}

/** Plain-English names for the base data types. Anything absent falls back. */
const typeNames: Record<string, string> = {
  Boolean: "true/false",
  Number: "number",
  Text: "text",
  Name: "text",
  Email: "email",
  Phone: "phone",
  Url: "link",
  Date: "date",
  DateTime: "date",
  Enum: "choice",
  Token: "choice",
  Object: "group of values",
  "Line Item": "product",
  "Trigger Product": "product",
  "Email Attachment": "attachment",
  "Url To Qr Code": "QR code",
};

/**
 * A merchant-readable name for what a variable holds — "true/false", "list of
 * links". Returns null for structured types that no short phrase describes.
 */
export function variableTypeName(dataType: string): string | null {
  const { Optional, Array } = genericPrefixes;
  const required = dataType.startsWith(Optional)
    ? dataType.slice(Optional.length)
    : dataType;
  if (required.startsWith(Array)) {
    const element = variableTypeName(required.slice(Array.length));
    return element ? `list of ${pluralize(element)}` : "list";
  }
  return typeNames[required] ?? null;
}

/** Pluralizes the head noun, so a nested list reads "lists of text". */
function pluralize(name: string): string {
  const [head, ...rest] = name.split(" of ");
  if (head === undefined || head === "true/false" || head === "text") {
    return name;
  }
  return [head.endsWith("s") ? head : `${head}s`, ...rest].join(" of ");
}
